import React, { lazy, useEffect, useState } from "react";

import "../src/assets/stylesheets/vendor/bootstrap.min.css";

import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "./store/store";
import PrivateRoute from "./routes/private-route";
import IdleTimer from "react-idle-timer";
import ConfirmExitModal from "./components/confirm-exit.modal";
import PageRoutes from "./constants/page-routes";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import LoadingSpinner from "./components/Element/LodingSpinner/LoadingSpinner";
import Theme from "./constants/theme";
import { Modal } from "antd";
import { handleGoBackToProductionSelection } from "./functions/CommonFunctions";
import DynamicFormRoute from "./routes/dynamic-form-route";

/**
 * @description This is used to lazy load the components of the application based on the route path
 * @type {React.LazyExoticComponent<React.ComponentType<any>>}
 */
const Landing = lazy(() => import("./modules/landing-page/Landing"));
const Welcome = lazy(() => import("./modules/welcome/Welcome"));
const Login = lazy(() => import("./modules/login/Login"));
const ExistingUser = lazy(() => import("./modules/existing-user/ExistingUser"));
const ProductSelection = lazy(() => import("./modules/product-selection/ProductSelection"));
const EditApplication = lazy(() => import("./modules/edit-application/EditApplication"));
const Console = lazy(() => import("./modules/console/Console"));
const Portal = lazy(() => import("./modules/portal/Portal"));
const Consent = lazy(() => import("./modules/consent/Consent"));
const ConsentOtp = lazy(() => import("./modules/consent/components/otp/ConsentOtp"));
const DynamicForm = lazy(() => import("./modules/dynamic-form/DynamicForm"));

const { confirm } = Modal;

function App() {
  /**
   * @description This function is used to set the theme of the application based on the url path name
   * and redirect to the home page
   */
  /*if (window.location.pathname === `/${Theme.DARAZ}`) {
    sessionStorage.setItem(Theme.KEY, Theme.DARAZ);
    window.location.replace("/");
  }*/

  /**
   * @description This function is used to purge the store if the path is root path i.e. "/"
   */
  if (window.location.pathname === "/") {
    persistor.purge();
  }

  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * @description This function is used to handle the back button of the browser and redirect to the home page
   * if the user is clicking on the back button from the browser
   */
  useEffect(() => {
    //Handle back btn
    /*window.addEventListener("popstate", () => {
      window.location.href = "/";
    });*/
    // showNotification("ERROR", "Hello");

    let popHandler = () => {
      confirm({
        title: `Would you like to return?`,
        content: "Would you like to return? Once you go back, please ensure that all data is removed.",
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        className: "confirm-popup",
        async onOk() {
          const response = await handleGoBackToProductionSelection();
          if (response) {
            window.location.href = `/${PageRoutes.PRODUCT_SELECTION}`;
          } else {
            window.location.href = "/";
          }
        },
        onCancel() {
          window.history.forward();
          setTimeout(() => {
            window.addEventListener("popstate", popHandler, { once: true });
          }, 50); // delay needed since the above is an async operation for some reason
        },
      });

      /*if (window.confirm('Go back?')) {
        window.location.href = "/";
      } else {
        window.history.forward()
        setTimeout(() => {
          window.addEventListener('popstate', popHandler, {once: true})
        }, 50) // delay needed since the above is an async operation for some reason
      }*/
    };
    window.addEventListener("popstate", popHandler, { once: true });
    window.history.pushState(null, null, null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`App`} data-theme={sessionStorage.getItem(Theme.KEY)}>
      <IdleTimer
        timeout={1000 * 60 * 20} // 20 minutes
        onIdle={() => setIsModalOpen(true)}
        debounce={250}
      />

      <LoadingSpinner />

      {isModalOpen && <ConfirmExitModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />}

      <React.Suspense fallback={<LoadingSpinner />}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <BrowserRouter>
              <Switch>
                <Route exact path="/linkedin" component={LinkedInCallback} />
                <Route exact path="/">
                  <Landing />
                </Route>

                <Route exact path={`/${PageRoutes.EDIT_APPLICATION}/:requestId`}>
                  <Landing />
                </Route>

                <PrivateRoute path={`/${PageRoutes.WELCOME}`} exact>
                  <Welcome />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.LOGIN}`} exact>
                  <Login />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.EXISTING_USER}`} exact>
                  <ExistingUser />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.PRODUCT_SELECTION}`} exact>
                  <ProductSelection />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.EDIT_APPLICATION}`} exact>
                  <EditApplication />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.CONSOLE}`} exact>
                  <Console />
                </PrivateRoute>

                <PrivateRoute path={`/${PageRoutes.PORTAL}`} exact>
                  <Portal />
                </PrivateRoute>

                <Route exact path={`/${PageRoutes.CONSENT}/:id`}>
                  <Consent />
                </Route>
                <Route exact path={`/${PageRoutes.CONSENT}/:id/${PageRoutes.OTP}`}>
                  <ConsentOtp />
                </Route>
                <DynamicFormRoute exact path={`/${PageRoutes.DYNAMIC_FORM}/:id`}>
                  <DynamicForm />
                </DynamicFormRoute>
              </Switch>
            </BrowserRouter>
          </PersistGate>
        </Provider>
      </React.Suspense>
    </div>
  );
}

export default App;
